import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines, faShieldCheck } from '@fortawesome/pro-regular-svg-icons';

import { getDashboardPanelTokens, rem } from '../dashboardPanelTokens';
import type { CoverageKind, CoverageStatus } from './coverage.types';
import { statusLabel } from './coverageStatus';
import { ABSENT, toneFor } from './coverageFormat';
import { getToneTokens } from './coverageTokens';

/**
 * The pieces the hero tile and the compact rows share.
 *
 * Both surfaces show the same facts at different weights, and writing them
 * twice is how a limit ends up labelled "Limit" in one place and "Aggregate
 * limit" in the other -- and how the duplication gate gets tripped.
 */

interface CoverageEyebrowProps {
  readonly kind: CoverageKind;
  readonly compact?: boolean;
}

/** Says what you are looking at before it says anything about it. A quote is
 *  not coverage, and the eyebrow is where that gets stated. */
export function CoverageEyebrow({
  kind,
  compact = false,
}: Readonly<CoverageEyebrowProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);
  const isQuote = kind === 'quote';
  const tone = getToneTokens('caution', palette.mode);

  return (
    <Typography
      component="p"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: rem(6),
        margin: 0,
        fontSize: rem(compact ? 10 : 11),
        fontWeight: 600,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: isQuote ? tone.ink : tokens.inkFaint,
      }}
    >
      <FontAwesomeIcon
        icon={isQuote ? faFileLines : faShieldCheck}
        style={{ fontSize: rem(compact ? 11 : 12) }}
      />
      {isQuote && !compact ? 'Quote · not coverage yet' : null}
      {isQuote && compact ? 'Quote' : null}
      {!isQuote ? 'Policy' : null}
    </Typography>
  );
}

interface CoverageStatusPillProps {
  readonly status: CoverageStatus;
  readonly compact?: boolean;
}

export function CoverageStatusPill({
  status,
  compact = false,
}: Readonly<CoverageStatusPillProps>) {
  const { palette } = useTheme();
  const tone = getToneTokens(toneFor(status), palette.mode);

  return (
    <Box
      component="span"
      data-qa="COVERAGE_STATUS_PILL"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: rem(6),
        flexShrink: 0,
        borderRadius: rem(999),
        padding: compact ? `${rem(1)} ${rem(8)}` : `${rem(2)} ${rem(10)}`,
        fontSize: rem(compact ? 11 : 12),
        fontWeight: 600,
        lineHeight: 1.5,
        color: tone.ink,
        backgroundColor: tone.surface,
      }}
    >
      {!compact && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: rem(6),
            height: rem(6),
            borderRadius: '50%',
            backgroundColor: 'currentColor',
          }}
        />
      )}
      {statusLabel(status)}
    </Box>
  );
}

export interface CoverageFact {
  readonly label: string;
  readonly value: string;
  readonly note?: string;
}

interface CoverageFactsProps {
  readonly facts: readonly CoverageFact[];
  readonly compact?: boolean;
}

/** The label / value / note triples, at hero weight or row weight. */
export function CoverageFacts({
  facts,
  compact = false,
}: Readonly<CoverageFactsProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);

  return (
    <Box
      component="dl"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: compact ? `${rem(8)} ${rem(28)}` : `${rem(20)} ${rem(28)}`,
        margin: 0,
        minWidth: 0,
      }}
    >
      {facts.map((fact) => (
        <Box key={fact.label} sx={{ minWidth: 0 }}>
          <Box
            component="dt"
            sx={{
              fontSize: rem(compact ? 10 : 11),
              fontWeight: 500,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: tokens.inkFaint,
              whiteSpace: 'nowrap',
            }}
          >
            {fact.label}
          </Box>
          <Box
            component="dd"
            sx={{
              margin: 0,
              marginTop: rem(compact ? 2 : 3),
              fontSize: rem(compact ? 13 : 19),
              fontWeight: compact ? 600 : 700,
              lineHeight: 1.25,
              letterSpacing: '-0.01em',
              fontVariantNumeric: 'tabular-nums',
              color: tokens.ink,
            }}
          >
            {fact.value}
          </Box>
          {!compact && fact.note && (
            <Box
              component="dd"
              sx={{
                margin: 0,
                marginTop: rem(2),
                fontSize: rem(11),
                color: tokens.inkFaint,
              }}
            >
              {fact.note}
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

interface CoverageTermBarProps {
  /** 0-1. Omitted where a bar would be meaningless -- an unknown status, an
   *  expired quote -- in which case the track alone is drawn. */
  readonly progress?: number;
  readonly active?: boolean;
  readonly label: string;
}

export function CoverageTermBar({
  progress,
  active = true,
  label,
}: Readonly<CoverageTermBarProps>) {
  const { palette } = useTheme();
  const tokens = getDashboardPanelTokens(palette.mode);
  const filled =
    progress === undefined ? 0 : Math.min(1, Math.max(0, progress));

  return (
    <Box
      role="img"
      aria-label={
        progress === undefined
          ? `${label}: progress unavailable`
          : `${label}: ${Math.round(filled * 100)} percent elapsed`
      }
      sx={{
        marginTop: rem(8),
        height: rem(6),
        width: '100%',
        borderRadius: rem(999),
        overflow: 'hidden',
        backgroundColor: tokens.line,
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${filled * 100}%`,
          borderRadius: rem(999),
          backgroundColor: active ? tokens.brand : tokens.inkFaint,
          transition: 'width 240ms cubic-bezier(0.23, 1, 0.32, 1)',
        }}
      />
    </Box>
  );
}

/** Absent renders as absent. Never a zero, never an invented date. */
export const orAbsent = (value: string | undefined): string => value ?? ABSENT;
